import React, { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eraser, RefreshCw, ShieldCheck, Trash2, X, Crown } from "lucide-react";
import { BACKEND_URL } from "../../lib/backend";

const API = `${BACKEND_URL}/api`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function TestUserCleanup({ dm, authHeader, onDone }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [includeUnconfirmed, setIncludeUnconfirmed] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreview = useCallback(async (incUnconfirmed) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/test-cleanup/preview?include_unconfirmed=${incUnconfirmed}`, { headers: authHeader() });
      setPreview(r.data);
    } catch (e) {
      toast.error("Failed to load cleanup preview");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  const openDialog = () => {
    setOpen(true); setResult(null); setConfirmText(""); setIncludeUnconfirmed(false);
    loadPreview(false);
  };

  const toggleUnconfirmed = (checked) => {
    setIncludeUnconfirmed(checked);
    loadPreview(checked);
  };

  const execute = async () => {
    setDeleting(true);
    try {
      const r = await axios.post(`${API}/admin/users/test-cleanup`,
        { include_unconfirmed: includeUnconfirmed, confirm: confirmText.trim() },
        { headers: authHeader() });
      setResult(r.data);
      toast.success(`Deleted ${r.data.deleted_users} test users`);
      onDone && onDone();
    } catch (e) {
      toast.error(`Cleanup failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = preview && preview.total > 0 && confirmText.trim().toUpperCase() === "ELIMINA" && !deleting;

  return (
    <>
      <button
        data-testid="cleanup-test-users-btn"
        onClick={openDialog}
        title="Delete auto-created test users (with preview + confirmation)"
        className={`flex items-center gap-2 px-4 py-2.5 rounded-full category-tag transition-all ${dm ? "bg-red-400/10 text-red-300 hover:bg-red-400/20" : "bg-red-50 text-red-700 hover:bg-red-100"}`}
      >
        <Eraser className="w-3.5 h-3.5" />Clean Test Users
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }} data-testid="cleanup-dialog-overlay">
          <div className={`w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${dm ? "border-white/5" : "border-black/5"}`}>
              <h3 className={`editorial-heading text-lg font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Clean Test Users</h3>
              <button data-testid="cleanup-close-btn" onClick={() => setOpen(false)} className={`p-1.5 rounded-lg ${dm ? "text-slate-500 hover:bg-[#252e2f]" : "text-[#6b7a7d] hover:bg-[#f3f4f5]"}`}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {result ? (
                <div data-testid="cleanup-result">
                  <p className={`text-sm font-medium mb-2 ${dm ? "text-emerald-400" : "text-emerald-700"}`}>
                    ✓ Deleted {result.deleted_users} test users
                  </p>
                  {Object.keys(result.related || {}).length > 0 && (
                    <p className={`category-tag mb-2 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                      Related records removed: {Object.entries(result.related).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                    </p>
                  )}
                  <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Reviewer and owner accounts were preserved.</p>
                </div>
              ) : (
                <>
                  <div className={`flex items-start gap-2 rounded-xl px-4 py-3 mb-4 ${dm ? "bg-emerald-400/5" : "bg-emerald-50"}`}>
                    <ShieldCheck className={`w-4 h-4 mt-0.5 flex-shrink-0 ${dm ? "text-emerald-400" : "text-emerald-600"}`} />
                    <p className={`category-tag ${dm ? "text-emerald-300" : "text-emerald-800"}`} data-testid="cleanup-protected-note">
                      Always protected: {(preview?.protected_emails || []).join(", ")}
                    </p>
                  </div>

                  <label className={`flex items-center gap-2 cursor-pointer category-tag mb-4 ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>
                    <input type="checkbox" checked={includeUnconfirmed} onChange={e => toggleUnconfirmed(e.target.checked)}
                      data-testid="cleanup-include-unconfirmed-checkbox"
                      className="w-3.5 h-3.5 accent-red-400 rounded" />
                    Also include unconfirmed (Pending) users older than 7 days
                  </label>

                  {loading ? (
                    <div className={`flex items-center gap-2 py-8 justify-center category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                      <RefreshCw className="w-4 h-4 animate-spin" />Loading preview…
                    </div>
                  ) : preview && (
                    <>
                      <p className={`category-tag mb-2 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} data-testid="cleanup-candidate-count">
                        {preview.total} account{preview.total === 1 ? "" : "s"} will be permanently deleted (users + magic tokens, feed prefs, settings, push and mobile records):
                      </p>
                      <div className={`rounded-xl max-h-64 overflow-y-auto ${dm ? "bg-[#141a1b]" : "bg-[#f8f9f9]"}`} data-testid="cleanup-candidate-list">
                        {preview.candidates.map(c => (
                          <div key={c.id || c.email} className={`flex items-center justify-between gap-2 px-4 py-2 border-b last:border-b-0 ${dm ? "border-white/4" : "border-black/4"}`}>
                            <span className={`text-xs font-mono truncate ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>{c.email}</span>
                            <span className="flex items-center gap-1.5 flex-shrink-0">
                              {c.is_admin && <Crown className={`w-3 h-3 ${dm ? "text-cyan-400" : "text-[#006874]"}`} title="Has admin flag" />}
                              <span className={`category-tag px-2 py-0.5 rounded-full ${c.reason === "unconfirmed" ? (dm ? "bg-amber-400/10 text-amber-400" : "bg-amber-50 text-amber-700") : (dm ? "bg-red-400/10 text-red-300" : "bg-red-50 text-red-600")}`}>
                                {c.reason === "unconfirmed" ? "pending" : "test"}
                              </span>
                              <span className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{fmtDate(c.created_at)}</span>
                            </span>
                          </div>
                        ))}
                        {preview.total === 0 && (
                          <p className={`px-4 py-6 text-center category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>No test users found — the list is already clean.</p>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {!result && (
              <div className={`px-6 py-4 border-t flex flex-col sm:flex-row sm:items-center gap-3 ${dm ? "border-white/5" : "border-black/5"}`}>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder='Type "ELIMINA" to confirm'
                  data-testid="cleanup-confirm-input"
                  className={`flex-1 px-4 py-2.5 rounded-xl text-sm border-0 outline-none ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] placeholder:text-[#bac9cc]"}`}
                />
                <button
                  data-testid="cleanup-execute-btn"
                  disabled={!canDelete}
                  onClick={execute}
                  className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-full category-tag transition-all disabled:opacity-40 disabled:cursor-not-allowed ${dm ? "bg-red-400 text-[#2b0505] hover:bg-red-300" : "bg-red-600 text-white hover:bg-red-700"}`}
                >
                  {deleting ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Deleting…</> : <><Trash2 className="w-3.5 h-3.5" />Delete {preview?.total ?? 0} users</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
